import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Api, useApiFormSubmit } from '../../api';
import { useForm } from 'react-typed-form';
import { formatISO } from 'date-fns';
import { FieldTextRequired } from '../../form';
import { useMutation } from 'api-read-hook';
import { produce } from 'immer';

interface CreateNoteModalProps {
  userId: string;
  onClose: () => void;
}

export default function CreateNoteModal({
  userId,
  onClose,
}: CreateNoteModalProps) {
  const { mutateExact } = useMutation();

  const form = useForm({
    defaultValues: {
      title: '',
      text: '',
      date: formatISO(new Date(), { representation: 'date' }),
    },
    onSubmit: useApiFormSubmit<Api.UserNote.W, Api.UserNote.V>(
      'POST',
      `/users/${userId}/notes`,
      {
        onSuccess: (responseData) => {
          toast.success('Note created');
          onClose();

          // Update data in User Dashboard without triggering full refresh
          mutateExact(
            `/users/${userId}/stats`,
            // @ts-expect-error todo[types]: solve issue
            (data: Api.UserStats.V) => {
              return produce(data, (draft) => {
                draft.notes = [
                  { ...responseData, addedByMe: true, addedByName: 'you' },
                  ...draft.notes,
                ];
              });
            },
          );
        },
      },
    ),
  });

  const canSubmit =
    form.getField('title').value?.trim() && form.getField('text').value?.trim();

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Create Note</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <FieldTextRequired
              field={form.getField('title')}
              fullWidth
              containerSx={{ flex: 1 }}
            />
            <FieldTextRequired
              field={form.getField('date')}
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
          </Stack>

          <FieldTextRequired
            field={form.getField('text')}
            label={null}
            fullWidth
            multiline
            rows={12}
            placeholder="Write your note content here..."
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={form.handleSubmit}
          variant="contained"
          disabled={!canSubmit || form.isLoading}
        >
          Create Note
        </Button>
      </DialogActions>
    </Dialog>
  );
}
